import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';

// API 기본 URL 가져오기
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8002';

// AuthContext 생성 및 export 추가
export const AuthContext = createContext();

// AuthProvider 컴포넌트
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  
  // 관리자 모드 관련 상태
  const [isAdminMode, setIsAdminMode] = useState(false);
  const [originalAdminUser, setOriginalAdminUser] = useState(null);
  const [impersonatedUser, setImpersonatedUser] = useState(null);
  
  // 그룹 관리자 모드 관련 상태
  const [isGroupOwnerMode, setIsGroupOwnerMode] = useState(false);
  const [originalGroupOwner, setOriginalGroupOwner] = useState(null);
  const [impersonatedGroupMember, setImpersonatedGroupMember] = useState(null);
  
  // API 호출 추적을 위한 ref
  const isCheckLoginCalled = useRef(false);

  // 공통 API 클라이언트 생성 함수
  const createApiClient = () => {
    const apiClient = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      withCredentials: true // 쿠키 전송을 위해 필수
    });
    
    // 요청 인터셉터 추가
    apiClient.interceptors.request.use(
      (config) => {
        // URL 경로 추출
        const url = config.url || '';
        const path = url.startsWith('/') ? url : `/${url}`;
        
        return config;
      },
      (error) => {
        console.error('[AuthContext] API 요청 인터셉터 오류:', error);
        return Promise.reject(error);
      }
    );
    
    // 응답 인터셉터 추가 (세션 만료 처리 및 자동 유저 정보 갱신)
    apiClient.interceptors.response.use(
      async (response) => {
        // 사용자 정보 갱신이 필요한 API 경로 목록 (운동 관련 제외)
        const userDataRefreshEndpoints = [
          '/api/v1/users/me',           // 프로필 수정
          '/api/v1/auth/user'           // 사용자 정보 관련 API (필요시 추가)
          // '/api/v1/workouts' 경로는 제외
        ];
        
        // 현재 API 경로 가져오기
        const url = response.config.url;
        // 요청 메서드 확인
        const method = response.config.method.toUpperCase();
        
        // PUT, POST, PATCH, DELETE 요청이고 사용자 정보 갱신이 필요한 엔드포인트인 경우
        const isUserModifyRequest = (method === 'PUT' || method === 'POST' || method === 'PATCH' || method === 'DELETE');
        const shouldRefreshUserData = userDataRefreshEndpoints.some(endpoint => url.includes(endpoint));
        
        if (isUserModifyRequest && shouldRefreshUserData) {
          try {
            // 세션 검증 API 호출하여 최신 사용자 정보 가져오기
            const sessionResponse = await api.get('/api/v1/auth/validate-session');
            
            if (sessionResponse.data.is_authenticated && sessionResponse.data.user) {
              const userData = sessionResponse.data.user;
              
              // 가장 모드에 따라 적절히 유저 정보 업데이트
              if (isAdminMode && impersonatedUser) {
                const updatedUserWithFlag = { ...userData, is_impersonated: true };
                setImpersonatedUser(updatedUserWithFlag);
                setUser(updatedUserWithFlag);
              } else if (isGroupOwnerMode && impersonatedGroupMember) {
                // 그룹 멤버 가장 모드는 갱신 안함
              } else {
                setUser(userData);
              }
            }
          } catch (error) {
            console.error('[AuthContext] 사용자 정보 자동 갱신 실패:', error);
          }
        }
        
        return response;
      },
      async (error) => {
        // 401 오류는 세션 만료를 의미할 수 있음
        if (error.response?.status === 401) {
          // 이중 가장 상태 확인
          const isDoubleImpersonation = sessionStorage.getItem('is_double_impersonation') === 'true';
          
          // 이중 가장 상태인 경우 모든 가장 정보 정리
          if (isDoubleImpersonation) {
            // console.log('[AuthContext] 이중 가장 상태에서 세션 만료 - 모든 가장 정보 정리');
            
            // 그룹 멤버 가장 정보 정리
            sessionStorage.removeItem('group_owner_mode');
            sessionStorage.removeItem('impersonated_group_member');
            sessionStorage.removeItem('original_group_owner');
            sessionStorage.removeItem('is_group_impersonating');
            sessionStorage.removeItem('is_double_impersonation');
          }
          
          // 가장 모드인 경우 세션 스토리지를 확인
          const isImpersonating = sessionStorage.getItem('is_impersonating') === 'true';
          const savedOriginalAdmin = sessionStorage.getItem('original_admin');
          
          if (isImpersonating && savedOriginalAdmin) {
            try {
              // 가장 모드 중지를 위한 서버 요청
              await api.post('/api/v1/auth/admin/stop-impersonate').catch(e => 
                console.warn('[AuthContext] 가장 모드 중지 요청 실패, 무시하고 계속:', e));
              
              // 세션 스토리지의 가장 관련 데이터 모두 제거
              sessionStorage.removeItem('impersonation_mode');
              sessionStorage.removeItem('impersonated_user');
              sessionStorage.removeItem('original_admin');
              sessionStorage.removeItem('is_impersonating');
              sessionStorage.removeItem('prev_is_impersonating');
              
              // 로컬 스토리지에서도 가장 관련 정보 제거
              localStorage.removeItem('adminMode');
              localStorage.removeItem('impersonatedUser');
              localStorage.removeItem('originalAdminUser');
              
              // 원래 관리자로 상태 복원 (세션 확인 전에)
              try {
                const originalAdmin = JSON.parse(savedOriginalAdmin);
                
                // 상태 업데이트 강제 적용
                setUser(originalAdmin);
                setIsLoggedIn(true);
                setIsAdminMode(false);
                setImpersonatedUser(null);
                setOriginalAdminUser(null);
              } catch (parseError) {
                console.error('[AuthContext] 원래 관리자 정보 파싱 실패:', parseError);
              }
              
              // 페이지 강제 새로고침 (캐시 무시)
              // window.location.href = '/?t=' + new Date().getTime();
              const currentPath = window.location.pathname;
              window.location.href = `${currentPath}?t=${new Date().getTime()}`;
              return Promise.reject(new Error('가장 모드가 해제되었습니다. 원래 계정으로 복원 중...'));
            } catch (e) {
              console.error('[AuthContext] 가장 모드 해제 실패:', e);
            }
          } else {
            // 일반적인 세션 만료 처리
            signOut();
            return Promise.reject(new Error('세션이 만료되었습니다. 다시 로그인해주세요.'));
          }
        }
        
        // 다른 모든 에러는 그대로 반환
        return Promise.reject(error);
      }
    );
    
    return apiClient;
  };

  // API 클라이언트 인스턴스
  const api = createApiClient();
  
  // 앱 초기화 시 로그인 상태 확인
  useEffect(() => {
    const checkLoginStatus = async () => {
      // 이미 호출된 경우 중복 호출 방지
      if (isCheckLoginCalled.current) {
        return;
      }
      
      // 호출 추적
      isCheckLoginCalled.current = true;
      
      try {
        setIsLoading(true);
        
        // 가장 모드 정보 유지하기
        const savedImpersonationMode = sessionStorage.getItem('impersonation_mode') === 'true';
        const savedOriginalAdmin = sessionStorage.getItem('original_admin');
        const savedImpersonatedUser = sessionStorage.getItem('impersonated_user');
        const isImpersonating = sessionStorage.getItem('is_impersonating') === 'true';
        
        // 그룹 관리자 가장 모드 정보
        const savedGroupOwnerMode = sessionStorage.getItem('group_owner_mode') === 'true';
        const savedOriginalGroupOwner = sessionStorage.getItem('original_group_owner');
        const savedImpersonatedGroupMember = sessionStorage.getItem('impersonated_group_member');
        const isGroupMemberImpersonating = sessionStorage.getItem('is_group_impersonating') === 'true';
        
        // 이중 가장 상태 확인
        const isDoubleImpersonation = sessionStorage.getItem('is_double_impersonation') === 'true';
        
        // console.log('[AUTH] 가장 모드 복원 시도:', { 
        //   isImpersonating, 
        //   isGroupMemberImpersonating,
        //   isDoubleImpersonation
        // });
        
        // 이중 가장 모드 복원 (어드민이 그룹 관리자로 가장한 후 그룹 멤버로 가장한 경우)
        if (isImpersonating && isGroupMemberImpersonating && isDoubleImpersonation &&
            savedOriginalAdmin && savedImpersonatedUser && savedImpersonatedGroupMember) {
          try {
            const originalAdmin = JSON.parse(savedOriginalAdmin);
            const impersonatedGroupOwner = JSON.parse(savedImpersonatedUser);
            const impersonatedMember = JSON.parse(savedImpersonatedGroupMember);
            
            // 플래그 추가
            const impersonatedUserWithFlag = {
              ...impersonatedGroupOwner,
              is_impersonated: true
            };
            
            const impersonatedMemberWithFlag = {
              ...impersonatedMember,
              is_group_impersonated: true
            };
            
            // 상태 복원 - 어드민, 그룹 관리자, 그룹 멤버 모두 설정
            setOriginalAdminUser(originalAdmin);
            setImpersonatedUser(impersonatedUserWithFlag);
            setOriginalGroupOwner(impersonatedUserWithFlag); // 그룹 관리자는 어드민이 가장한 사용자
            setImpersonatedGroupMember(impersonatedMemberWithFlag);
            setUser(impersonatedMemberWithFlag); // 현재 사용자는 가장된 그룹 멤버
            setIsAdminMode(true);
            setIsGroupOwnerMode(true);
            setIsLoggedIn(true);
            
            // console.log('[AUTH] 이중 가장 모드 복원 성공');
          } catch (error) {
            console.error('[AuthContext] 이중 가장 모드 복원 실패:', error);
          }
        }
        // 가장 모드 정보가 모두 있을 경우, 세션 검증 전에 먼저 가장 모드 복원 시도
        else if (isImpersonating && savedImpersonationMode && savedOriginalAdmin && savedImpersonatedUser) {
          try {
            const originalAdmin = JSON.parse(savedOriginalAdmin);
            const impersonatedUser = JSON.parse(savedImpersonatedUser);
            
            // 가장된 사용자 설정 - 원본 데이터에 가장된 플래그 추가
            const impersonatedUserWithFlag = {
              ...impersonatedUser,
              is_impersonated: true  // 가장된 사용자임을 표시하는 플래그
            };
            
            // 상태 업데이트 (세션 검증 전에 먼저 설정하여 화면 깜빡임 방지)
            setOriginalAdminUser(originalAdmin);
            setImpersonatedUser(impersonatedUserWithFlag);
            setUser(impersonatedUserWithFlag);
            setIsLoggedIn(true);
            setIsAdminMode(true);
          } catch (error) {
            console.error('[AuthContext] 가장 모드 임시 복원 실패:', error);
          }
        }
        // 그룹 관리자 가장 모드도 확인하여 복원
        else if (isGroupMemberImpersonating && savedGroupOwnerMode && savedOriginalGroupOwner && savedImpersonatedGroupMember) {
          try {
            const originalGroupOwner = JSON.parse(savedOriginalGroupOwner);
            const impersonatedMember = JSON.parse(savedImpersonatedGroupMember);
            
            // 가장된 그룹 멤버 설정 - 원본 데이터에 가장된 플래그 추가
            const impersonatedMemberWithFlag = {
              ...impersonatedMember,
              is_group_impersonated: true  // 그룹 관리자에 의해 가장된 사용자임을 표시
            };
            
            // 상태 업데이트 (세션 검증 전에 먼저 설정하여 화면 깜빡임 방지)
            setOriginalGroupOwner(originalGroupOwner);
            setImpersonatedGroupMember(impersonatedMemberWithFlag);
            setUser(impersonatedMemberWithFlag);
            setIsLoggedIn(true);
            setIsGroupOwnerMode(true);
          } catch (error) {
            console.error('[AuthContext] 그룹 가장 모드 임시 복원 실패:', error);
          }
        }
        
        // 세션 검증 API 호출
        try {
          const response = await api.get('/api/v1/auth/validate-session');
          
          // 응답 데이터 확인
          const data = response.data;
          
          if (data.is_authenticated && data.user) {
            // 로그인된 상태: 사용자 정보 설정
            
            // user 객체에 is_admin이 없는 경우에만 기본값 설정
            if (data.user && data.user.is_admin === undefined) {
              // 하위 호환성 유지: 최상위 is_admin이 있으면 사용, 없으면 기본값 false
              data.user.is_admin = data.is_admin || false;
            }
            
            // 가장 모드인 경우 세션 스토리지 정보 복원
            if (isImpersonating && savedImpersonationMode && savedOriginalAdmin && savedImpersonatedUser) {
              try {
                const originalAdmin = JSON.parse(savedOriginalAdmin);
                const impersonatedUser = JSON.parse(savedImpersonatedUser);
                
                // 가장된 사용자 설정 - 원본 데이터에 가장된 플래그 추가
                const impersonatedUserWithFlag = {
                  ...impersonatedUser,
                  is_impersonated: true  // 가장된 사용자임을 표시하는 플래그
                };
                
                // 상태 업데이트
                setOriginalAdminUser(originalAdmin);
                setImpersonatedUser(impersonatedUserWithFlag);
                setUser(impersonatedUserWithFlag);  // 현재 사용자를 가장된 사용자로 설정
                setIsAdminMode(true);
                
                setIsLoading(false);
                return;
              } catch (error) {
                console.error('[AuthContext] 가장 모드 복원 중 오류 발생:', error);
                // 가장 모드 초기화는 하지 않음 - 오류가 나더라도 일반 모드로 진행
              }
            }
            
            // 일반적인 로그인 처리
            setUser(data.user);
            setIsLoggedIn(true);
          } else {
            // 로그인되지 않은 상태
            
            // 가장 모드 정보가 있으면 원래 관리자로 복원 시도
            if (isImpersonating && savedOriginalAdmin) {
              try {
                const originalAdmin = JSON.parse(savedOriginalAdmin);
                
                // 원래 관리자로 설정하여 로그인 유지 시도
                setUser(originalAdmin);
                setIsLoggedIn(true);
                setIsAdminMode(false);
                setImpersonatedUser(null);
                setOriginalAdminUser(null);
                
                // 가장 모드 정보 제거
                sessionStorage.removeItem('impersonation_mode');
                sessionStorage.removeItem('impersonated_user');
                sessionStorage.removeItem('original_admin');
                sessionStorage.removeItem('is_impersonating');
                
                // window.location.href = '/';
                const currentPath = window.location.pathname;
                window.location.href = `${currentPath}?t=${new Date().getTime()}`;
                return;
              } catch (error) {
                console.error('[AuthContext] 원래 관리자로 복원 실패:', error);
              }
            }
            
            setUser(null);
            setIsLoggedIn(false);
            
            // 로그인 실패 시에만 가장 정보 제거
            sessionStorage.removeItem('impersonation_mode');
            sessionStorage.removeItem('impersonated_user');
            sessionStorage.removeItem('original_admin');
            sessionStorage.removeItem('is_impersonating');
          }
        } catch (error) {
          console.error('[AuthContext] 세션 검증 API 호출 오류:', error);
          
          // 401 오류 (인증 실패) 처리
          if (error.response && error.response.status === 401) {
            // 가장 모드인 경우 가장 모드를 유지하거나 원래 계정으로 돌아감
            if (isImpersonating && savedOriginalAdmin) {
              try {
                // 가장 모드 세션만 만료되었을 가능성이 높으므로 원래 관리자로 복원
                const originalAdmin = JSON.parse(savedOriginalAdmin);
                
                // 세션 스토리지의 가장 정보 제거
                sessionStorage.removeItem('impersonation_mode');
                sessionStorage.removeItem('impersonated_user');
                sessionStorage.removeItem('is_impersonating');
                sessionStorage.removeItem('original_admin');
                
                // 상태 업데이트 - 가장 모드 비활성화, 원래 관리자로 설정
                setUser(originalAdmin);
                setIsLoggedIn(true);
                setIsAdminMode(false);
                setImpersonatedUser(null);
                setOriginalAdminUser(null);
                
                // 이벤트 발생 - 가장 모드 종료 알림
                window.dispatchEvent(new CustomEvent('user-impersonation-ended', {
                  detail: { originalAdmin }
                }));
                
                // 복원 후 원래 계정으로 서버와 동기화하기 위해 새로고침
                // window.location.href = '/';
                const currentPath = window.location.pathname;
                window.location.href = `${currentPath}?t=${new Date().getTime()}`;
                
                return;
              } catch (error) {
                console.error('[AuthContext] 가장 모드 해제 중 오류 발생:', error);
              }
            }
            // 그룹 관리자 가장 모드인 경우
            else if (isGroupMemberImpersonating && savedOriginalGroupOwner) {
              try {
                // 가장 모드 세션만 만료되었을 가능성이 높으므로 원래 그룹 관리자로 복원
                const originalGroupOwner = JSON.parse(savedOriginalGroupOwner);
                
                // 세션 스토리지의 가장 정보 제거
                sessionStorage.removeItem('group_owner_mode');
                sessionStorage.removeItem('impersonated_group_member');
                sessionStorage.removeItem('is_group_impersonating');
                sessionStorage.removeItem('original_group_owner');
                
                // 상태 업데이트 - 가장 모드 비활성화, 원래 그룹 관리자로 설정
                setUser(originalGroupOwner);
                setIsLoggedIn(true);
                setIsGroupOwnerMode(false);
                setImpersonatedGroupMember(null);
                setOriginalGroupOwner(null);
                
                // 이벤트 발생 - 그룹 가장 모드 종료 알림
                window.dispatchEvent(new CustomEvent('group-member-impersonation-ended', {
                  detail: { originalGroupOwner }
                }));
                
                // 복원 후 원래 계정으로 서버와 동기화하기 위해 새로고침
                // window.location.href = '/';
                const currentPath = window.location.pathname;
                window.location.href = `${currentPath}?t=${new Date().getTime()}`;
                
                return;
              } catch (error) {
                console.error('[AuthContext] 그룹 가장 모드 해제 중 오류 발생:', error);
              }
            }
          }
          
          // API 오류 발생 시 가장 모드 정보가 있으면 원래 관리자로 복원 시도
          if (isImpersonating && savedOriginalAdmin) {
            try {
              const originalAdmin = JSON.parse(savedOriginalAdmin);
              
              // 원래 관리자로 설정하여 로그인 유지 시도
              setUser(originalAdmin);
              setIsLoggedIn(true);
              setIsAdminMode(false);
              setImpersonatedUser(null);
              setOriginalAdminUser(null);
              
              // 가장 모드 정보 제거
              sessionStorage.removeItem('impersonation_mode');
              sessionStorage.removeItem('impersonated_user');
              sessionStorage.removeItem('original_admin');
              sessionStorage.removeItem('is_impersonating');
              
              setIsLoading(false);
              
              // 복원 후 원래 계정으로 서버와 동기화하기 위해 새로고침
              // window.location.href = '/';
              const currentPath = window.location.pathname;
              window.location.href = `${currentPath}?t=${new Date().getTime()}`;
              return;
            } catch (e) {
              console.error('[AuthContext] API 오류 상황에서 원래 관리자 복원 실패:', e);
            }
          }
          
          // 오류 발생 시 로그아웃 처리
          setUser(null);
          setIsLoggedIn(false);
          
          // 가장 정보 제거
          sessionStorage.removeItem('impersonation_mode');
          sessionStorage.removeItem('impersonated_user');
          sessionStorage.removeItem('original_admin');
          sessionStorage.removeItem('is_impersonating');
          sessionStorage.removeItem('prev_is_impersonating');
          
          // 그룹 관리자 가장 모드 정보도 제거
          sessionStorage.removeItem('group_owner_mode');
          sessionStorage.removeItem('impersonated_group_member');
          sessionStorage.removeItem('original_group_owner');
          sessionStorage.removeItem('is_group_impersonating');
          
          // 가장 상태 초기화
          setIsAdminMode(false);
          setImpersonatedUser(null);
          setOriginalAdminUser(null);
          setIsGroupOwnerMode(false);
          setImpersonatedGroupMember(null);
          setOriginalGroupOwner(null);
        } finally {
          setIsLoading(false);
        }
      } catch (outerError) {
        console.error('[AuthContext] 전체 로그인 확인 과정 오류:', outerError);
        setIsLoading(false);
        setUser(null);
        setIsLoggedIn(false);
      }
    };

    checkLoginStatus();
  }, []);

  // 사용자 설정 함수 (콜백에서 사용)
  const updateUser = (userData) => {
    if (!userData) {
      console.warn('[AuthContext] 사용자 정보 없음');
      signOut();
      return;
    }
    
    setUser(userData);
    setIsLoggedIn(true);
  };

  // 관리자 권한 확인
  const isAdmin = () => {
    // 원래 어드민인 경우
    if (originalAdminUser) return true;
    
    // 현재 유저가 어드민인지 확인
    if (!user) return false;
    return user.is_admin === true || user.role === 'admin';
  };

  // 사용자가 그룹 관리자(소유자)인지 확인하는 함수
  const isGroupOwner = () => {
    // 사용자가 없는 경우
    if (!user) return false;
    
    // 원래 그룹 관리자인 경우 (가장 모드 중인 경우도 포함)
    if (isGroupOwnerMode && originalGroupOwner) {
      return true;
    }
    
    // 가장된 사용자가 그룹 관리자인 경우 (어드민이 그룹 관리자로 가장한 경우)
    if (isAdminMode && impersonatedUser) {
      return impersonatedUser.is_group_owner || 
             (impersonatedUser.owned_groups && impersonatedUser.owned_groups.length > 0);
    }
    
    // 현재 사용자가 그룹 관리자인지 확인 (is_group_owner는 user 객체 바깥에 있음)
    return user.is_group_owner || 
           (user.owned_groups && user.owned_groups.length > 0);
  };
  
  // 현재 사용자가 관리자인 그룹 ID 목록 반환
  const getOwnedGroupIds = () => {
    if (!user) return [];
    
    // 사용자가 그룹 관리자인 경우
    if (user.owned_groups && Array.isArray(user.owned_groups)) {
      return user.owned_groups;
    }
    
    return [];
  };

  // 가장 모드나 그룹 가장 모드를 고려한 현재 사용자 반환
  const getCurrentUser = () => {
    // 현재 페이지 경로
    const currentPath = window.location.pathname;
    
    // 특별 처리 경로 확인 (홈, 대시보드, Today 페이지)
    const isSpecialPath = currentPath === '/home' || currentPath === '/dashboard';
    
    // 이중 가장 상태 확인 (어드민이 그룹 관리자로 가장한 후 그룹 멤버로 가장한 경우)
    const isDoubleImpersonation = isAdminMode && isGroupOwnerMode && 
                                  impersonatedUser && impersonatedGroupMember;
    
    // 이중 가장 모드인 경우 (최우선 처리)
    if (isDoubleImpersonation) {
      // 특별 경로에서는 가장된 멤버를 표시
      if (isSpecialPath) {
        return impersonatedGroupMember;
      } else {
        // 다른 페이지에서는 1차 가장된 그룹 관리자로 표시
        return impersonatedUser;
      }
    }
    
    // 어드민이 다른 사용자를 가장하는 경우 - 모든 페이지에서 가장된 사용자로 표시
    if (isAdminMode && impersonatedUser) {
      return impersonatedUser;
    }
    
    // 그룹 관리자가 멤버를 가장하는 경우 - 홈페이지와 대시보드에서만 가장된 사용자로 표시
    if (isGroupOwnerMode && impersonatedGroupMember) {
      // 특별 경로에서만 가장된 멤버로 표시
      if (isSpecialPath) {
        return impersonatedGroupMember;
      } else {
        // 다른 페이지에서는 원래 그룹 관리자로 표시
        return originalGroupOwner;
      }
    }
    
    return user;
  };

  // 가장 모드 시작
  const impersonateUser = async (userId) => {
    try {
      // 이미 가장 중인 경우 경고 표시
      if (isAdminMode && impersonatedUser) {
        alert('이미 다른 사용자로 가장 중입니다. 먼저 가장 모드를 해제하세요.');
        return false;
      }
      
      // 가장 API 호출 (API 인스턴스 사용)
      const response = await api.post(`/api/v1/auth/admin/impersonate/${userId}`);
      
      // 응답 데이터 가져오기
      const data = response.data;
      
      if (data.success && data.user) {
        // 원래 관리자 정보 저장
        const adminUser = { ...user };
        
        // 세션 스토리지에 가장 모드 정보 저장 (페이지를 새로고침해도 유지)
        sessionStorage.setItem('impersonation_mode', 'true');
        sessionStorage.setItem('original_admin', JSON.stringify(adminUser));
        sessionStorage.setItem('impersonated_user', JSON.stringify(data.user));
        sessionStorage.setItem('is_impersonating', 'true');
        
        // 가장된 사용자 설정 - 원본 데이터에 가장된 플래그 추가
        const impersonatedUserWithFlag = {
          ...data.user,
          is_impersonated: true  // 가장된 사용자임을 표시하는 플래그
        };
        
        // 상태 업데이트
        setOriginalAdminUser(adminUser);
        setImpersonatedUser(impersonatedUserWithFlag);
        setUser(impersonatedUserWithFlag);  // 현재 사용자를 가장된 사용자로 변경
        setIsAdminMode(true);
        
        // 이벤트 발생 - 앱 전체에 사용자 변경 알림
        window.dispatchEvent(new CustomEvent('user-impersonation-changed', { 
          detail: { 
            isImpersonating: true,
            impersonatedUser: impersonatedUserWithFlag
          } 
        }));
        
        // 세션 저장소에 변경사항이 적용될 시간을 줌 (500ms)
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // // 페이지 새로고침 (캐시 방지를 위해 타임스탬프 추가)
        // window.location.href = `/?t=${new Date().getTime()}`;
        const currentPath = window.location.pathname;
        window.location.href = `${currentPath}?t=${new Date().getTime()}`;
        
        return true;
      } else {
        console.error('가장 모드 활성화 실패: 유효한 응답이 아님');
        return false;
      }
    } catch (error) {
      console.error('가장 모드 활성화 중 오류 발생:', error);
      return false;
    }
  };

  // 로그아웃 함수
  const signOut = async () => {
    try {
      // 백엔드 로그아웃 API 호출
      await api.post('/api/v1/auth/logout');
    } catch (error) {
      console.error('[AuthContext] 로그아웃 API 요청 실패:', error);
    }
    
    // 클라이언트 상태 초기화
    setUser(null);
    setIsLoggedIn(false);
    setIsAdminMode(false);
    setOriginalAdminUser(null);
    setImpersonatedUser(null);
    
    // 로컬 스토리지 및 세션 스토리지 정리
    localStorage.removeItem('adminMode');
    localStorage.removeItem('impersonatedUser');
    localStorage.removeItem('originalAdminUser');
    sessionStorage.removeItem('impersonation_mode');
    sessionStorage.removeItem('impersonated_user');
    sessionStorage.removeItem('original_admin');
    sessionStorage.removeItem('is_impersonating');
    
    // 홈페이지로 리디렉션
    window.location.href = '/';
  };

  // 로그인 URL 가져오기 및 리디렉션
  const signIn = async () => {
    try {
      // 환경에 따라 리다이렉트 URI 설정
      const redirectUri = process.env.NODE_ENV === 'production'
        ? 'https://app.voice-fit.net/auth/callback'
        : 'http://localhost:3000/auth/callback';
      
      // 로그인 URL 가져오기 API 호출
      const response = await api.get(`/api/v1/auth/login-url?redirect_uri=${encodeURIComponent(redirectUri)}`);
      
      const data = response.data;
      
      if (!data.login_url) {
        throw new Error('로그인 URL이 응답에 포함되어 있지 않습니다');
      }
      
      // Cognito 호스팅 로그인 페이지로 리다이렉션 (현재 창에서)
      window.location.assign(data.login_url);
    } catch (error) {
      console.error('[AuthContext] 로그인 URL 가져오기 실패:', error);
    }
  };

  // 그룹 멤버로 가장하는 함수
  const impersonateGroupMember = async (userId) => {
    try {
      // 그룹 관리자가 아니면 거부 (어드민이 그룹 관리자로 가장한 경우도 허용)
      if (!isGroupOwner()) {
        console.error('[AuthContext] 그룹 관리자만 멤버를 가장할 수 있습니다.');
        return false;
      }
      
      // 현재 사용자 정보 백업 (어드민이 그룹 관리자로 가장한 경우 impersonatedUser를 사용)
      const originalUser = isAdminMode ? { ...impersonatedUser } : { ...user };
      
      // 그룹 멤버 가장 API 호출
      const response = await api.post(`/api/v1/auth/group-owner/impersonate/${userId}`);
      
      if (response.status !== 200) {
        throw new Error('그룹 멤버 가장 실패');
      }
      
      // 가장 대상 사용자 정보
      const impersonatedMember = response.data.user;
      
      // 그룹 멤버 정보에 가장 플래그 추가
      const impersonatedMemberWithFlag = {
        ...impersonatedMember,
        is_group_impersonated: true // 그룹 관리자 가장 모드임을 표시
      };
      
      // 세션 스토리지에 원래 그룹 관리자 정보와 가장 상태 저장
      sessionStorage.setItem('original_group_owner', JSON.stringify(originalUser));
      sessionStorage.setItem('impersonated_group_member', JSON.stringify(impersonatedMember));
      sessionStorage.setItem('group_owner_mode', 'true');
      sessionStorage.setItem('is_group_impersonating', 'true');
      
      // 이중 가장인 경우(어드민이 그룹 관리자로 가장한 후 그룹 멤버로 가장)
      if (isAdminMode && impersonatedUser) {
        sessionStorage.setItem('is_double_impersonation', 'true');
      }
      
      // 상태 업데이트
      setOriginalGroupOwner(originalUser);
      setImpersonatedGroupMember(impersonatedMemberWithFlag);
      setUser(impersonatedMemberWithFlag);
      setIsGroupOwnerMode(true);
      
      // 이벤트 발생 - 그룹 멤버 가장 시작 알림
      window.dispatchEvent(new CustomEvent('group-member-impersonation-started', {
        detail: { 
          originalUser: originalUser, 
          impersonatedMember: impersonatedMemberWithFlag 
        }
      }));
      
      // 세션 저장소에 변경사항이 적용될 시간을 줌 (500ms)
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // 페이지 새로고침 (현재 위치 유지하며 캐시 방지를 위해 타임스탬프 추가)
      // 홈 또는 대시보드로 이동하여 가장된 사용자 데이터로 새로고침
      const currentPath = window.location.pathname;
      if ((currentPath === '/home' || currentPath === '/dashboard') && !isAdminMode) {
        // 현재 경로가 홈이나 대시보드면 그냥 새로고침
        window.location.href = `${currentPath}?t=${new Date().getTime()}`;
      } else {
        // 다른 경로면 홈으로 이동 (가장된 사용자 데이터 보기)
        window.location.href = `/home?t=${new Date().getTime()}`;
      }
      
      return true;
    } catch (error) {
      console.error('[AuthContext] 그룹 멤버 가장 실패:', error);
      return false;
    }
  };
  
  // 그룹 멤버 가장 중지 함수
  const stopGroupMemberImpersonation = async () => {
    try {
      // 그룹 관리자 가장 모드가 아니면 거부
      if (!isGroupOwnerMode || !originalGroupOwner) {
        console.error('[AuthContext] 그룹 관리자 가장 모드가 아닙니다.');
        return false;
      }
      
      // 이중 가장 상태 확인
      const isDoubleImpersonation = sessionStorage.getItem('is_double_impersonation') === 'true';
      
      // 세션 스토리지에서 원래 그룹 관리자 정보 가져오기
      const savedOriginalGroupOwner = sessionStorage.getItem('original_group_owner');
      if (!savedOriginalGroupOwner) {
        throw new Error('원래 그룹 관리자 정보를 찾을 수 없습니다');
      }
      
      // 그룹 멤버 가장 중지 API 호출
      try {
        const response = await api.post('/api/v1/auth/group-owner/stop-impersonate');
        // 성공 여부 확인
        if (response.status !== 200) {
          console.warn('[AuthContext] 그룹 멤버 가장 중지 API 응답 실패');
        }
      } catch (error) {
        console.error('[AuthContext] 그룹 멤버 가장 중지 API 호출 실패:', error);
        // API 호출 실패해도 계속 진행 (클라이언트에서라도 상태 정리)
      }
      
      // 원래 그룹 관리자 정보 복원
      const originalUser = JSON.parse(savedOriginalGroupOwner);
      
      // 세션 스토리지에서 가장 관련 데이터 즉시 제거
      sessionStorage.removeItem('group_owner_mode');
      sessionStorage.removeItem('impersonated_group_member');
      sessionStorage.removeItem('original_group_owner');
      sessionStorage.removeItem('is_group_impersonating');
      sessionStorage.removeItem('is_double_impersonation');
      
      // 이중 가장 상태인 경우, 그룹 관리자(어드민이 가장한)로 복원
      if (isDoubleImpersonation && isAdminMode && originalAdminUser) {
        // 상태 업데이트 - 어드민 가장 모드는 유지하고 그룹 멤버 가장만 해제
        setUser(originalUser);
        setIsGroupOwnerMode(false);
        setImpersonatedGroupMember(null);
        setOriginalGroupOwner(null);
      } else {
        // 일반 그룹 멤버 가장 해제
        setUser(originalUser);
        setIsGroupOwnerMode(false);
        setImpersonatedGroupMember(null);
        setOriginalGroupOwner(null);
      }
      
      // 이벤트 발생 - 그룹 멤버 가장 종료 알림
      window.dispatchEvent(new CustomEvent('group-member-impersonation-ended', {
        detail: { originalUser }
      }));
      
      // 세션 저장소에 변경사항이 적용될 시간을 줌 (500ms)
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // // 페이지 새로고침 (캐시 방지를 위해 타임스탬프 추가)
      // window.location.href = `${window.location.pathname}?t=${new Date().getTime()}`;
      const currentPath = window.location.pathname;
      if ((currentPath === '/profile' || currentPath === '/group') && !isAdminMode) {
        // 현재 경로가 홈이나 대시보드면 그냥 새로고침
        // window.location.href = `${currentPath}?t=${new Date().getTime()}`;
      } else {
        // 다른 경로면 홈으로 이동 (가장된 사용자 데이터 보기)
        window.location.href = `${currentPath}?t=${new Date().getTime()}`;
      }
      
      return true;
    } catch (error) {
      console.error('[AuthContext] 그룹 멤버 가장 중지 실패:', error);
      return false;
    }
  };

  // 가장 모드 중지 (어드민이 가장 종료)
  const stopImpersonation = async () => {
    try {
      // 이중 가장 상태 확인
      const isDoubleImpersonation = sessionStorage.getItem('is_double_impersonation') === 'true';
      
      // 원래 어드민 정보 백업 (화면에 표시되기 전에 null이 될 수 있음)
      let adminUserBackup = originalAdminUser ? { ...originalAdminUser } : null;
      
      if (!adminUserBackup) {
        // 세션 스토리지에서 다시 시도
        const savedOriginalAdmin = sessionStorage.getItem('original_admin');
        if (savedOriginalAdmin) {
          try {
            const parsedAdmin = JSON.parse(savedOriginalAdmin);
            if (parsedAdmin) {
              adminUserBackup = parsedAdmin;
            }
          } catch (e) {
            console.error('[AUTH] 세션 스토리지 어드민 정보 파싱 실패:', e);
          }
        }
      }
      
      // 서버에 가장 중지 API 호출
      try {
        await api.post('/api/v1/auth/admin/stop-impersonate');
      } catch (error) {
        console.error('[AUTH] 서버 가장 중지 API 호출 실패:', error);
        // API 실패해도 계속 진행
      }
      
      if (isDoubleImpersonation) {
        // 모든 가장 관련 세션 스토리지 정리
        sessionStorage.removeItem('group_owner_mode');
        sessionStorage.removeItem('impersonated_group_member');
        sessionStorage.removeItem('original_group_owner');
        sessionStorage.removeItem('is_group_impersonating');
        sessionStorage.removeItem('is_double_impersonation');
      }
      
      // 어드민 가장 정보 세션 스토리지에서 제거
      sessionStorage.removeItem('impersonation_mode');
      sessionStorage.removeItem('impersonated_user');
      sessionStorage.removeItem('original_admin');
      sessionStorage.removeItem('is_impersonating');
      sessionStorage.removeItem('prev_is_impersonating');
      
      // 상태 초기화 - 순서 중요
      setIsGroupOwnerMode(false);
      setImpersonatedGroupMember(null);
      setOriginalGroupOwner(null);
      
      setIsAdminMode(false);
      setImpersonatedUser(null);
      
      // 마지막으로 원래 어드민 사용자로 설정 - 이 부분이 중요
      if (adminUserBackup) {
        setUser(adminUserBackup);
      } else {
        console.error('[AUTH] 원래 어드민 정보가 없어 설정 불가');
      }
      
      // 원래 어드민 정보 초기화 (원래 어드민으로 사용자 설정 후)
      setOriginalAdminUser(null);
      
      // 이벤트 발생
      window.dispatchEvent(new CustomEvent('user-impersonation-ended', {
        detail: { originalAdmin: adminUserBackup }
      }));
      
      // 세션 저장소에 변경사항이 적용될 시간을 줌 (500ms)
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // // 페이지 새로고침 (캐시 방지를 위해 타임스탬프 추가)
      // window.location.href = `/?t=${new Date().getTime()}`;
      const currentPath = window.location.pathname;
      window.location.href = `${currentPath}?t=${new Date().getTime()}`;
      return true;
    } catch (error) {
      console.error('[AUTH] 가장 중지 실패:', error);
      return false;
    }
  };

  // 사용자 정보 새로고침 함수
  const refreshUserData = async () => {
    try {
      // 로딩 상태 설정 안함 (백그라운드 갱신)
      
      // 세션 검증 API 호출
      const response = await api.get('/api/v1/auth/validate-session');
      
      // 응답 데이터 확인
      const data = response.data;
      
      if (data.is_authenticated && data.user) {
        // 가장 모드일 때는 사용자 정보만 갱신 (가장 모드 유지)
        if (isAdminMode && impersonatedUser) {
          // 가장된 사용자 정보 갱신
          const updatedUserWithFlag = {
            ...data.user,
            is_impersonated: true
          };
          
          setImpersonatedUser(updatedUserWithFlag);
          setUser(updatedUserWithFlag);
          return true;
        } else if (isGroupOwnerMode && impersonatedGroupMember) {
          // 그룹 멤버 가장 모드일 때는 업데이트 안함 (혼란 방지)
          return false;
        } else {
          // 일반 모드일 때는 사용자 정보 갱신
          setUser(data.user);
          return true;
        }
      }
      
      return false;
    } catch (error) {
      console.error('[AuthContext] 사용자 정보 갱신 실패:', error);
      return false;
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      isLoading,
      isLoggedIn,
      isAdminMode,
      originalAdminUser,
      impersonatedUser,
      isGroupOwnerMode,
      impersonatedGroupMember,
      originalGroupOwner,
      api,
      signIn,
      signOut,
      isAdmin,
      isGroupOwner,
      getOwnedGroupIds,
      getCurrentUser,
      updateUser,
      impersonateUser,
      impersonateGroupMember,
      stopGroupMemberImpersonation,
      stopImpersonation,
      refreshUserData
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// useAuth 훅 (Context 사용 편의를 위해)
export const useAuth = () => {
  return useContext(AuthContext);
};